/**
 * Insurance section navigation — the single source of truth for each
 * persona's sections.
 *
 * Two consumers render from this map:
 *   · the app shells' sidebar "Insurance" group (desktop — the ONLY
 *     section nav ≥ md)
 *   · <SectionTabs> — the in-page pill bar each dashboard shows on
 *     mobile (< md), where the sidebar isn't available
 *
 * Section keys double as the URL segment: /insurance/:persona/:section.
 * Keep each list in the dashboard's reading order.
 */

import {
  Activity,
  Banknote,
  BedDouble,
  Building2,
  Calculator,
  ClipboardCheck,
  ClipboardList,
  Copy,
  FileText,
  Gauge,
  Gavel,
  Grid3x3,
  Handshake,
  HeartPulse,
  Home,
  Hospital,
  Inbox,
  LayoutDashboard,
  LineChart,
  QrCode,
  ScanSearch,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import type { Persona } from './persona';

export interface InsuranceSection<K extends string = string> {
  key: K;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const PATIENT_SECTIONS = [
  { key: 'home', label: 'Home', icon: Home },
  { key: 'enquiry', label: 'Get care', icon: Stethoscope },
  { key: 'estimates', label: 'Estimates', icon: Sparkles },
  { key: 'checkin', label: 'Check-in', icon: QrCode },
  { key: 'opd', label: 'OPD', icon: Wallet },
  { key: 'claims', label: 'Claims', icon: FileText },
  { key: 'pass', label: 'Pass', icon: QrCode },
  { key: 'employer', label: 'My employer', icon: Building2 },
] as const;

/**
 * Leadership first, then the desks that do the work. The first four sections
 * are the hospital owner's four levers — where the hospital stands, what is
 * arriving, where inflow comes from, and when the money lands; everything
 * after them is the operational queue that was already here.
 */
export const HOSPITAL_SECTIONS = [
  { key: 'command', label: 'Command centre', icon: Gauge },
  { key: 'capacity', label: 'Capacity', icon: Hospital },
  { key: 'demand', label: 'Demand', icon: TrendingUp },
  { key: 'cash', label: 'Cash velocity', icon: Banknote },
  { key: 'overview', label: 'Work queue', icon: LayoutDashboard },
  { key: 'enquiries', label: 'Enquiries', icon: Gavel },
  { key: 'drafts', label: 'Draft check-ins', icon: ClipboardCheck },
  { key: 'preauth', label: 'Pre-auth', icon: ClipboardList },
  { key: 'upgrades', label: 'Upgrades', icon: BedDouble },
  { key: 'room-change', label: 'Room change', icon: BedDouble },
  { key: 'coding', label: 'Coding', icon: Stethoscope },
  // 'revenue' was a four-stat receivables card; Cash velocity above is a
  // strict superset of it (same figures, plus ageing buckets, payer-level
  // settlement performance and the benchmark comparison), so the tab is gone
  // rather than duplicated.
  { key: 'claims', label: 'Claims', icon: FileText },
] as const;

/**
 * The insurer CXO reads top-down: the command centre states the position,
 * the next three sections are the entities the position decomposes into
 * (accounts, providers, claims), then the surveillance and forward-looking
 * surfaces, then the working desks that were already here. Keep that order —
 * it is the drill-down hierarchy the persona is designed around.
 */
export const INSURER_SECTIONS = [
  { key: 'command', label: 'Command centre', icon: Gauge },
  { key: 'corporates', label: 'Corporate book', icon: Building2 },
  { key: 'hospitals', label: 'Hospital risk', icon: Hospital },
  { key: 'claims', label: 'Claims', icon: FileText },
  { key: 'leakage', label: 'Leakage', icon: ShieldCheck },
  { key: 'interventions', label: 'Interventions', icon: HeartPulse },
  { key: 'trajectory', label: 'Trajectory', icon: TrendingUp },
  { key: 'decisions', label: 'Decision queue', icon: LayoutDashboard },
  { key: 'preauth', label: 'Pre-auth requests', icon: ScanSearch },
  { key: 'portfolio', label: 'Loss ratio', icon: LineChart },
  { key: 'cohorts', label: 'Cohorts', icon: Grid3x3 },
  { key: 'pricing', label: 'Pricing', icon: Calculator },
  { key: 'renewals', label: 'Renewals', icon: Handshake },
  { key: 'endorsements', label: 'Endorsements', icon: ClipboardList },
  { key: 'duplicates', label: 'Duplicates', icon: Copy },
  { key: 'utilization', label: 'Utilization', icon: Activity },
  { key: 'settlements', label: 'Settlements', icon: Banknote },
] as const;

export const TPA_SECTIONS = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'preauth', label: 'Pre-auth requests', icon: ScanSearch },
  { key: 'claims', label: 'Claims', icon: FileText },
  { key: 'utilization', label: 'Utilization', icon: Activity },
] as const;

export const EMPLOYER_SECTIONS = [
  { key: 'ipd', label: 'IPD · Hospitalisation', icon: Hospital },
  { key: 'opd', label: 'OPD · Out-patient', icon: Stethoscope },
  { key: 'insights', label: 'Insights', icon: LineChart },
  { key: 'plan', label: 'Health plan', icon: ClipboardCheck },
  { key: 'people', label: 'People & cover', icon: Users },
  { key: 'account', label: 'Account', icon: Wallet },
] as const;

// A broker reads its clients' aggregates only — no IPD/OPD claim lists, no
// people, no health plan: those carry names or are HR's own decisions.
export const BROKER_SECTIONS = [
  { key: 'insights', label: 'Client insights', icon: LineChart },
  { key: 'account', label: 'Campaigns & CD', icon: Wallet },
] as const;

// AdminLeads is a single-view inbox — one section, no in-page tabs.
export const ADMIN_SECTIONS = [{ key: 'leads', label: 'Leads', icon: Inbox }] as const;

export const SUPERADMIN_SECTIONS = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'provisioning', label: 'Provisioning', icon: Building2 },
  { key: 'utilization', label: 'Utilization', icon: Activity },
] as const;

export const INSURANCE_NAV: Record<Persona, readonly InsuranceSection[]> = {
  patient: PATIENT_SECTIONS,
  hospital: HOSPITAL_SECTIONS,
  insurer: INSURER_SECTIONS,
  tpa: TPA_SECTIONS,
  employer: EMPLOYER_SECTIONS,
  broker: BROKER_SECTIONS,
  admin: ADMIN_SECTIONS,
  superadmin: SUPERADMIN_SECTIONS,
};
